import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MachineContext } from '../DataContexts/MachineContext';
import { DataContext } from '../DataContexts/DataContext';
import PlaylistAssign from '../PlaylistManager/PlaylistAssign';
import AdsAssign from '../PlaylistManager/AdsAssign';
import BackgroundAssign from '../PlaylistManager/BackgroundAssign';
import useDeepLinkedSection from '../navigation/useDeepLinkedSection';
import DashboardSection from '../components/DashboardSection';
import '../PlaylistManager/PlaylistAssign.css';

const Blocklists = () => {
  const { machines, loading, error, removeFromBlacklist, addToBlacklist } = useContext(MachineContext);
  const { artists } = useContext(DataContext);
  const [search, setSearch] = useState('');
  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const navigate = useNavigate();

  // "Go to Account" (DJB-215): opens the selected machine on the Accounts
  // overview sub-page (?tab=list) with that account preselected, the same
  // deep link the Dashboard widgets use (MachineManager reads ?id=).
  const handleGoToAccount = (machineId) => {
    navigate(`/machines?tab=list&id=${encodeURIComponent(machineId)}`);
  };

  // Collapsed-section logic for the subpage's sections (Playlist
  // Subscriptions, Ads & Screensaver Subscriptions, Background subscriptions,
  // Blocked Content): each opens and closes independently and all start
  // collapsed; a collapsed header carries a muted-blue info line (DJB-83).
  const [openSections, setOpenSections] = useState(() => new Set());
  const toggleSection = (key) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  const blocklistOpen = openSections.has('blocklist');

  // DJB-112: a section picked from the left nav arrives as ?section=<anchor>
  // and must open even though sections default to collapsed
  useDeepLinkedSection(
    {
      'section-playlist-subscriptions': 'subscriptions',
      'section-ad-subscriptions': 'ad-subscriptions',
      'section-background-subscriptions': 'background-subscriptions',
      'section-blocked-content': 'blocklist',
    },
    (key) => setOpenSections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
  );

  // Sorting state
  const [artistSortAsc, setArtistSortAsc] = useState(true);
  const [trackSortKey, setTrackSortKey] = useState('title');
  const [trackSortAsc, setTrackSortAsc] = useState(true);

  // Filter machines by name, id, or city (case-insensitive)
  const filteredMachines = useMemo(() => {
    return machines.filter(machine =>
      machine.name.toLowerCase().includes(search.toLowerCase()) ||
      machine.id.toLowerCase().includes(search.toLowerCase()) ||
      (machine.city && machine.city.toLowerCase().includes(search.toLowerCase()))
    );
  }, [machines, search]);

  const selectedMachine = useMemo(() => {
    return machines.find(m => m.id === selectedMachineId);
  }, [machines, selectedMachineId]);

  // Memoize all tracks preparation to avoid recalculation
  const allTracks = useMemo(() => {
    if (artists.length === 0) return [];

    const tracks = [];
    artists.forEach(artist => {
      artist.albums.forEach(album => {
        album.tracks.forEach(track => {
          // Filter out deleted tracks
          if (!track.deleted) {
            tracks.push({
              id: track.id,
              title: track.title,
              artistName: artist.name,
              tags: track.tags || []
            });
          }
        });
      });
    });
    return tracks;
  }, [artists]);

  // Memoize track lookup map for faster blocked track processing
  const trackIdToInfo = useMemo(() => {
    const lookup = {};
    artists.forEach(artist => {
      artist.albums.forEach(album => {
        album.tracks.forEach(track => {
          // Filter out deleted tracks
          if (!track.deleted) {
            lookup[track.id] = {
              title: track.title,
              artistName: artist.name,
              id: track.id
            };
          }
        });
      });
    });
    return lookup;
  }, [artists]);

  // Memoize artist ID set for faster lookups
  const artistIdSet = useMemo(() => {
    return new Set(artists.map(a => a.id));
  }, [artists]);

  // Prepare blocked artists and tracks - memoized to prevent recalculation
  const { blockedArtists, blockedTracks, blockedTrackIds, blockedArtistIds } = useMemo(() => {
    if (!selectedMachine || !selectedMachine.blacklist || artists.length === 0) {
      return { blockedArtists: [], blockedTracks: [], blockedTrackIds: new Set(), blockedArtistIds: new Set() };
    }

    const blockedArtists = [];
    const blockedTracks = [];

    selectedMachine.blacklist.forEach(id => {
      if (artistIdSet.has(id)) {
        // It's an artist
        const artist = artists.find(a => a.id === id);
        if (artist) blockedArtists.push({ name: artist.name, id: artist.id });
      } else if (trackIdToInfo[id]) {
        // It's a track
        blockedTracks.push({ ...trackIdToInfo[id], id });
      }
    });

    // Sort blocked artists
    blockedArtists.sort((a, b) =>
      artistSortAsc
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );

    // Sort blocked tracks
    blockedTracks.sort((a, b) => {
      if (trackSortKey === 'title') {
        return trackSortAsc
          ? a.title.localeCompare(b.title)
          : b.title.localeCompare(a.title);
      } else {
        return trackSortAsc
          ? a.artistName.localeCompare(b.artistName)
          : b.artistName.localeCompare(a.artistName);
      }
    });

    const blockedTrackIds = new Set(blockedTracks.map(t => t.id));
    const blockedArtistIds = new Set(blockedArtists.map(a => a.id));

    return { blockedArtists, blockedTracks, blockedTrackIds, blockedArtistIds };
  }, [selectedMachine, artists, artistIdSet, trackIdToInfo, artistSortAsc, trackSortKey, trackSortAsc]);

  // Remove from blacklist handler
  const handleRemove = (id) => {
    if (selectedMachine) {
      removeFromBlacklist(selectedMachine.id, [id]);
    }
  };

  const [trackSearch, setTrackSearch] = useState('');
  const [allTracksSortKey, setAllTracksSortKey] = useState('title');
  const [allTracksSortAsc, setAllTracksSortAsc] = useState(true);

  // Filter tracks by search (title, artist, or tag name) - memoized
  const { tags: allTags = [] } = useContext(DataContext);
  const filteredAndSortedTracks = useMemo(() => {
    if (!selectedMachine) return [];

    const searchLower = trackSearch.toLowerCase().trim();
    const matches = (track) => {
      const tagNames = (track.tags || [])
        .map(tagId => allTags.find(t => t.id === tagId))
        .filter(Boolean)
        .map(tag => tag.name.toLowerCase());
      return (
        track.title.toLowerCase().includes(searchLower) ||
        track.artistName.toLowerCase().includes(searchLower) ||
        tagNames.some(tagName => tagName.includes(searchLower))
      );
    };

    const filtered = allTracks.filter(
      track => !blockedTrackIds.has(track.id) && matches(track)
    );

    // Deduplicate by id (same track can appear in multiple albums)
    const byId = new Map();
    filtered.forEach(t => { if (!byId.has(t.id)) byId.set(t.id, t); });
    const unique = Array.from(byId.values());

    return unique.sort((a, b) => {
      let comparison = 0;
      if (allTracksSortKey === 'artistName') {
        comparison = a.artistName.toLowerCase().localeCompare(b.artistName.toLowerCase());
        if (comparison === 0) {
          comparison = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
        }
      } else if (allTracksSortKey === 'title') {
        comparison = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
        if (comparison === 0) {
          comparison = a.artistName.toLowerCase().localeCompare(b.artistName.toLowerCase());
        }
      } else if (allTracksSortKey === 'tags') {
        const aTags = (a.tags.map(tagId => {
          const tag = allTags.find(t => t.id === tagId);
          return tag ? tag.name.toLowerCase() : '';
        }).join(', '));
        const bTags = (b.tags.map(tagId => {
          const tag = allTags.find(t => t.id === tagId);
          return tag ? tag.name.toLowerCase() : '';
        }).join(', '));
        comparison = aTags.localeCompare(bTags);
      }
      return allTracksSortAsc ? comparison : -comparison;
    });
  }, [allTracks, blockedTrackIds, trackSearch, allTracksSortKey, allTracksSortAsc, allTags, selectedMachine]);

  const TRACKS_PAGE_SIZE = 50;
  const [tracksPage, setTracksPage] = useState(1);

  useEffect(() => {
    setTracksPage(1);
  }, [trackSearch, allTracksSortKey, allTracksSortAsc]);

  const visibleTracks = filteredAndSortedTracks.slice(0, tracksPage * TRACKS_PAGE_SIZE);

  const tracksListRef = useRef(null);

  const handleTracksScroll = (event) => {
    const el = event.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      if (visibleTracks.length < filteredAndSortedTracks.length) {
        setTracksPage(page => page + 1);
      }
    }
  };

  const toggleAllTracksSort = (key) => {
    if (allTracksSortKey === key) {
      setAllTracksSortAsc(a => !a);
    } else {
      setAllTracksSortKey(key);
      setAllTracksSortAsc(true);
    }
  };

  // Artist states
  const [artistSearch, setArtistSearch] = useState('');
  const [allArtistsSortKey, setAllArtistsSortKey] = useState('name');
  const [allArtistsSortAsc, setAllArtistsSortAsc] = useState(true);

  // Memoized filtered and sorted artists
  const filteredAndSortedArtists = useMemo(() => {
    if (!selectedMachine) return [];
    const filtered = artists.filter(artist => {
      if (blockedArtistIds.has(artist.id)) return false;
      return artist.name.toLowerCase().includes(artistSearch.toLowerCase());
    });
    return filtered.sort((a, b) => {
      let comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      return allArtistsSortAsc ? comparison : -comparison;
    });
  }, [artists, blockedArtistIds, artistSearch, allArtistsSortAsc, selectedMachine]);

  const toggleAllArtistsSort = (key) => {
    if (allArtistsSortKey === key) {
      setAllArtistsSortAsc(a => !a);
    } else {
      setAllArtistsSortKey(key);
      setAllArtistsSortAsc(true);
    }
  };

  const ARTISTS_PAGE_SIZE = 50;
  const [artistsPage, setArtistsPage] = useState(1);

  useEffect(() => {
    setArtistsPage(1);
  }, [artistSearch, filteredAndSortedArtists.length, allArtistsSortKey, allArtistsSortAsc]);

  const visibleArtists = filteredAndSortedArtists.slice(0, artistsPage * ARTISTS_PAGE_SIZE);

  const artistsListRef = useRef(null);

  const handleArtistsScroll = (event) => {
    const el = event.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      if (visibleArtists.length < filteredAndSortedArtists.length) {
        setArtistsPage(page => page + 1);
      }
    }
  };

  const sortArrow = (isActive, asc) =>
    isActive ? <span className="ps-sort-arrow">{asc ? '▲' : '▼'}</span> : null;

  return (
    <div className="ps-page">
      <PlaylistAssign
        active={openSections.has('subscriptions')}
        onToggle={() => toggleSection('subscriptions')}
      />
      <AdsAssign
        active={openSections.has('ad-subscriptions')}
        onToggle={() => toggleSection('ad-subscriptions')}
      />
      {/* DJB-216/228: managed backgrounds subscribed to machines */}
      <BackgroundAssign
        active={openSections.has('background-subscriptions')}
        onToggle={() => toggleSection('background-subscriptions')}
      />
      <DashboardSection
        id="section-blocked-content"
        title="Blocked Content"
        info="Blokkeer nummers of artiesten per machine."
        isOpen={blocklistOpen}
        onToggle={() => toggleSection('blocklist')}
        bodyId="ps-body-blocklist"
      >
          <div className="ps-columns">
            {/* First column: search bar and tinted list of machines */}
            <div className="ps-column ps-column--machines">
              <div className="ps-column-head">
                <span className="ps-column-label">Machines</span>
                <span className="ps-column-count">{filteredMachines.length}</span>
              </div>
              <div className="ps-filters">
                <input
                  type="text"
                  className="ps-input"
                  placeholder="Search by name, ID, or place..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="ps-list-scroll">
                <ul className="ps-machine-list">
                  {loading && <li className="ps-empty">Loading...</li>}
                  {error && <li className="ps-empty ps-status--error">{error}</li>}
                  {!loading && !error && filteredMachines.length === 0 && (
                    <li className="ps-empty">No matching machines</li>
                  )}
                  {!loading && !error && filteredMachines.map(machine => {
                    // Calculate blocklist count for this machine
                    let blocklistCount = 0;
                    if (machine.blacklist && artists.length > 0) {
                      let countArtists = 0, countTracks = 0;
                      machine.blacklist.forEach(id => {
                        if (artistIdSet.has(id)) countArtists++;
                        else countTracks++;
                      });
                      blocklistCount = countArtists + countTracks;
                    }
                    return (
                      <li
                        key={machine.id}
                        className={`ps-machine-item${selectedMachineId === machine.id ? ' selected' : ''}`}
                        onClick={() => setSelectedMachineId(machine.id)}
                      >
                        <div className="ps-machine-row">
                          <span className="ps-machine-name">{machine.name}</span>
                          <span className="ps-count-badge">{blocklistCount} blocked</span>
                        </div>
                        {/* Machine id intentionally not rendered (DJB-66) but the
                            search above still matches on it */}
                        <div className="ps-machine-meta">
                          <span className="ps-machine-place">{machine.city}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            {/* Second column: selected machine info and blocked artists/tracks */}
            <div className="ps-column ps-column--blocked">
              {selectedMachine ? (
                <>
                  <div className="ps-selected">
                    <div className="ps-selected-row">
                      <div className="ps-selected-name">
                        {selectedMachine.name} – {selectedMachine.city}
                      </div>
                      <button
                        type="button"
                        className="ps-btn ps-btn--primary"
                        title="Open this account on the Accounts page"
                        onClick={() => handleGoToAccount(selectedMachine.id)}
                      >
                        Go to Account
                      </button>
                    </div>
                    <div className="ps-selected-id">
                      Machine id: <span>{selectedMachine.id}</span>
                    </div>
                  </div>
                  <div className="ps-column-head">
                    <h3 className="ps-column-label">Blocked artists</h3>
                    <span className="ps-column-count">{blockedArtists.length}</span>
                  </div>
                  <div className="ps-table-scroll ps-table-scroll--blocked-artists">
                    <table className="ps-table">
                      <thead>
                        <tr>
                          <th className="ps-th-sort" onClick={() => setArtistSortAsc(a => !a)}>
                            Artist name {sortArrow(true, artistSortAsc)}
                          </th>
                          <th className="ps-td-action"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {blockedArtists.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="ps-empty-cell">None</td>
                          </tr>
                        ) : (
                          blockedArtists.map((artist) => (
                            <tr key={artist.id}>
                              <td>{artist.name}</td>
                              <td className="ps-td-action">
                                <button
                                  className="ps-icon-btn ps-icon-btn--remove"
                                  title="Remove from blocklist"
                                  onClick={() => handleRemove(artist.id)}
                                >-</button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="ps-column-head ps-column-head--stacked">
                    <h3 className="ps-column-label">Blocked tracks</h3>
                    <span className="ps-column-count">{blockedTracks.length}</span>
                  </div>
                  <div className="ps-table-scroll ps-table-scroll--blocked-tracks">
                    <table className="ps-table">
                      <thead>
                        <tr>
                          <th
                            className="ps-th-sort"
                            onClick={() => {
                              setTrackSortKey('title');
                              setTrackSortAsc(a => trackSortKey === 'title' ? !trackSortAsc : true);
                            }}
                          >
                            Track title {sortArrow(trackSortKey === 'title', trackSortAsc)}
                          </th>
                          <th
                            className="ps-th-sort"
                            onClick={() => {
                              setTrackSortKey('artistName');
                              setTrackSortAsc(a => trackSortKey === 'artistName' ? !trackSortAsc : true);
                            }}
                          >
                            Artist {sortArrow(trackSortKey === 'artistName', trackSortAsc)}
                          </th>
                          <th className="ps-td-action"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {blockedTracks.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="ps-empty-cell">None</td>
                          </tr>
                        ) : (
                          blockedTracks.map((track) => (
                            <tr key={track.id}>
                              <td>{track.title}</td>
                              <td>{track.artistName}</td>
                              <td className="ps-td-action">
                                <button
                                  className="ps-icon-btn ps-icon-btn--remove"
                                  title="Remove from blocklist"
                                  onClick={() => handleRemove(track.id)}
                                >-</button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="ps-placeholder">Select a machine to see details</div>
              )}
            </div>
            {/* Third column: full artist and track catalogue with search */}
            <div className="ps-column ps-column--catalog">
              {!selectedMachine ? (
                <div className="ps-placeholder">Select a machine to see details</div>
              ) : (
                <>
                  <div className="ps-column-head">
                    <h3 className="ps-column-label">Artists</h3>
                    <span className="ps-column-count">{filteredAndSortedArtists.length}</span>
                  </div>
                  <div className="ps-toolbar">
                    <input
                      type="text"
                      className="ps-input"
                      placeholder="Search artists by name..."
                      value={artistSearch}
                      onChange={e => setArtistSearch(e.target.value)}
                    />
                  </div>
                  <div
                    className="ps-table-scroll ps-table-scroll--all-artists"
                    ref={artistsListRef}
                    onScroll={handleArtistsScroll}
                  >
                    <table className="ps-table">
                      <thead>
                        <tr>
                          <th className="ps-th-sort" onClick={() => toggleAllArtistsSort('name')}>
                            Name {sortArrow(allArtistsSortKey === 'name', allArtistsSortAsc)}
                          </th>
                          <th className="ps-td-action"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleArtists.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="ps-empty-cell">No artists found</td>
                          </tr>
                        ) : (
                          visibleArtists.map(artist => (
                            <tr key={artist.id}>
                              <td>{artist.name}</td>
                              <td className="ps-td-action">
                                <button
                                  className="ps-icon-btn ps-icon-btn--add"
                                  title="Add artist to blocklist"
                                  onClick={() => addToBlacklist(selectedMachine.id, [artist.id])}
                                >+</button>
                              </td>
                            </tr>
                          ))
                        )}
                        {visibleArtists.length < filteredAndSortedArtists.length && (
                          <tr>
                            <td colSpan={2} className="ps-empty-cell ps-loading-cell">Loading more...</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="ps-column-head ps-column-head--stacked">
                    <h3 className="ps-column-label">Tracks</h3>
                    <span className="ps-column-count">{filteredAndSortedTracks.length}</span>
                  </div>
                  <div className="ps-toolbar">
                    <input
                      type="text"
                      className="ps-input"
                      placeholder="Search tracks by title, artist, or tag..."
                      value={trackSearch}
                      onChange={e => {
                        setTrackSearch(e.target.value);
                        setTracksPage(1);
                      }}
                    />
                  </div>
                  <div
                    className="ps-table-scroll ps-table-scroll--all-tracks"
                    ref={tracksListRef}
                    onScroll={handleTracksScroll}
                  >
                    <table className="ps-table" key={`blocklist-tracks-${trackSearch}`}>
                      <thead>
                        <tr>
                          <th className="ps-th-sort" onClick={() => toggleAllTracksSort('title')}>
                            Title {sortArrow(allTracksSortKey === 'title', allTracksSortAsc)}
                          </th>
                          <th className="ps-th-sort" onClick={() => toggleAllTracksSort('artistName')}>
                            Artist {sortArrow(allTracksSortKey === 'artistName', allTracksSortAsc)}
                          </th>
                          <th className="ps-th-sort" onClick={() => toggleAllTracksSort('tags')}>
                            Tags {sortArrow(allTracksSortKey === 'tags', allTracksSortAsc)}
                          </th>
                          <th className="ps-td-action"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleTracks.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="ps-empty-cell">No tracks found</td>
                          </tr>
                        ) : (
                          visibleTracks.map(track => (
                            <tr key={track.id}>
                              <td>{track.title}</td>
                              <td>{track.artistName}</td>
                              <td>
                                <div className="ps-tag-list">
                                  {track.tags.map(tagId => {
                                    const tag = allTags.find(t => t.id === tagId);
                                    return tag ? (
                                      <span key={tag.id} className="ps-tag-chip">{tag.name}</span>
                                    ) : null;
                                  })}
                                </div>
                              </td>
                              <td className="ps-td-action">
                                <button
                                  className="ps-icon-btn ps-icon-btn--add"
                                  title="Add to blocklist"
                                  onClick={() => addToBlacklist(selectedMachine.id, [track.id])}
                                >+</button>
                              </td>
                            </tr>
                          ))
                        )}
                        {visibleTracks.length < filteredAndSortedTracks.length && (
                          <tr>
                            <td colSpan={4} className="ps-empty-cell ps-loading-cell">Loading more...</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
      </DashboardSection>
    </div>
  );
};

export default Blocklists;
